// Novel V3C1: read-only, on-screen measurement witness.
// Counts come ONLY from TextbookLiveChapterPageFlow's actual DOM column metrics.
// These are not physical print-page counts, a measured PDF or production authority.
const clean = (v) => String(v ?? "").trim();
const list = (v) => Array.isArray(v) ? v : [];
const asObject = (v) => v && typeof v === "object" && !Array.isArray(v) ? v : {};

export function novelBrowserUnitKey(kind, sourceId) {
  const role = clean(kind);
  const id = clean(sourceId);
  return ["front", "chapter", "back"].includes(role) && id
    ? role + ":" + id : "";
}

export function novelBrowserWitnessSourceKey({
  bookFormat = {}, frontMatter = [], chapters = [], backMatter = [], sceneById,
} = {}) {
  const format = asObject(bookFormat);
  // For preview cache invalidation ONLY: all original saved rich content,
  // chapter assignments and Canon text are represented in this local token.
  // This is deliberately NOT a cryptographic production fingerprint.
  const material = JSON.stringify([
    clean(format.trimSize), clean(format.orientation), clean(format.bleed),
    list(frontMatter).map((matter) => [matter?.id, matter?.title, matter?.text, matter?.manuscriptContent]),
    list(chapters).map((chapter) => [
      chapter?.id, chapter?.title,
      list(chapter?.sceneIds).map((rawId) => {
        const id = clean(rawId);
        const scene = sceneById?.get?.(id) || sceneById?.get?.(rawId) || null;
        return [id, scene?.text, scene?.manuscriptContent,
          scene?.illustrationUrl, scene?.illustrationStatus];
      }),
    ]),
    list(backMatter).map((matter) => [matter?.id, matter?.title, matter?.text, matter?.manuscriptContent]),
  ]);
  let hash = 2166136261;
  for (let i = 0; i < material.length; i += 1) {
    hash ^= material.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return "v3c1-local-" + material.length + "-" + (hash >>> 0).toString(16);
}

export function summarizeNovelBrowserPageEvidenceV3C({
  sections = [], observed = {},
} = {}) {
  const counts = asObject(observed);
  let nextStart = 1;
  let completed = 0;
  let validEntries = 0;
  const seen = new Set();

  const units = list(sections).map((section, index) => {
    const kind = clean(section?.kind);
    const id = clean(section?.id ?? section?.sourceId);
    const key = novelBrowserUnitKey(kind, id);
    const unique = Boolean(key && !seen.has(key));
    if (key) seen.add(key);
    if (unique) validEntries += 1;
    const value = unique ? counts[key] : null;
    const pageCount = Number.isInteger(value) && value >= 1 && value <= 100000
      ? value : null;
    if (pageCount != null) completed += 1;
    const projectedStart = pageCount != null ? nextStart : null;
    const projectedEnd = projectedStart != null ? projectedStart + pageCount - 1 : null;
    if (projectedEnd == null) nextStart = null;
    else nextStart = projectedEnd + 1;
    return {
      key,
      position: index + 1,
      kind,
      sourceId: id,
      title: clean(section?.title ?? section?.item?.title),
      browserPageCount: pageCount,
      projectedStart: projectedStart,
      projectedEnd: projectedEnd,
      startPolicy: "next_page",
      measurementKind: "browser_dom_projection_only",
      // Not final physical pages, even if all units have been visited.
      verifiedPhysicalStart: null,
      verifiedPhysicalPageCount: null,
    };
  });
  const complete = units.length > 0 && completed === units.length &&
    validEntries === units.length;
  return {
    schemaVersion: "novel-browser-page-evidence-v3c1",
    measurementKind: "provisional_browser_dom_projection",
    sectionCount: units.length,
    observedSectionCount: completed,
    pendingSectionCount: units.length - completed,
    sectionIdentitiesComplete: validEntries === units.length,
    allSectionsObserved: complete,
    units,
    // All-unit total is still only an on-screen projection; never a final PDF count.
    projectedBrowserPageCount: complete ? nextStart - 1 : null,
    physicalInteriorMeasured: false,
    measuredInteriorPdf: null,
    finalPageCount: null,
    finalPageCountLocked: false,
    marginPolicyConverged: false,
    manuscriptLocked: false,
    spineWidthInches: null,
    exportEnabled: false,
    publicationEnabled: false,
  };
}

export function recordNovelBrowserPageEvidenceV3C(current, {
  sourceKey = "", unitKey = "", pageCount,
} = {}) {
  const key = clean(sourceKey);
  const unit = clean(unitKey);
  const previous = asObject(current);
  if (!key || !novelBrowserUnitKey(...unit.split(":").slice(0, 2)) ||
    !Number.isInteger(pageCount) || pageCount < 1 || pageCount > 100000) {
    return previous;
  }
  const oldCounts = previous.sourceKey === key ? asObject(previous.observed) : {};
  if (previous.sourceKey === key && oldCounts[unit] === pageCount) return previous;
  return { sourceKey: key, observed: { ...oldCounts, [unit]: pageCount } };
}
